import type { TrackingEvent } from '../../types';
import { isLeadEvent } from '../../utils/conversionEvents';
import { getOpenAiEvent, type OpenAiEventData } from './utils';

/**
 * OpenAI's standard lead event. Reported alongside every lead type so a
 * campaign has one aggregate signal to optimise against — the same rationale as
 * Meta's derived `Lead`.
 */
const LEAD_EVENT: OpenAiEventData = {
    eventName: 'lead_created',
    isCustom: false,
    dataType: 'customer_action',
};

/**
 * Every ChatGPT Ads event to report for one logged conversion.
 *
 * Lead types produce their granular event plus `lead_created`. A created
 * enquiry and a sent meeting request already map to `lead_created`, so the
 * derivation skips adding it again.
 *
 * There is no client-specific variant: that exists only because Meta runs a
 * second client pixel, and we ship a single ArchiPro ChatGPT Ads pixel.
 *
 * All derived events share the caller's deduplication id. OpenAI deduplicates
 * on pixel id, event name and event id together.
 */
export function deriveOpenAiEvents(event: TrackingEvent): OpenAiEventData[] {
    const base = getOpenAiEvent(event);
    if (!base) {
        return [];
    }

    const events: OpenAiEventData[] = [base];

    if (isLeadEvent(event) && base.eventName !== LEAD_EVENT.eventName) {
        events.push(LEAD_EVENT);
    }

    return events;
}
